import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import api from '../../api';
import { useAuth } from '../../auth/authContext';
import {
  ApplicationButton,
  ApplicationStateRegion,
} from '../application';
import AVIcon from '../icons/AllVoicedIcons';
import BaseModalApp from '../modals/app-modals/BaseModalApp';
import { getSafeSupportAppArea } from './supportContext';

export const SUPPORT_CATEGORIES = Object.freeze({
  problem: {
    icon: 'alert',
    titleKey: 'support_report_problem',
    title: 'Report a problem',
    descriptionKey: 'support_report_problem_description',
    description: "Something isn’t working as expected.",
    guidanceKey: 'support_problem_guidance',
    guidance: 'Please describe what happened and, if relevant, what you expected to happen.',
    subject: '[All-Voiced] Problem report',
  },
  question: {
    icon: 'help',
    titleKey: 'support_ask_question',
    title: 'Ask a question',
    descriptionKey: 'support_ask_question_description',
    description: 'Need help using All-Voiced?',
    guidanceKey: 'support_question_guidance',
    guidance: 'What can we help you with?',
    subject: '[All-Voiced] Support question',
  },
  feedback: {
    icon: 'lightbulb',
    titleKey: 'support_share_feedback',
    title: 'Share feedback',
    descriptionKey: 'support_share_feedback_description',
    description: 'Have an idea or suggestion?',
    guidanceKey: 'support_feedback_guidance',
    guidance: 'Tell us your idea or suggestion.',
    subject: '[All-Voiced] Feedback',
  },
});

export default function HelpSupportModal({ isOpen, onRequestClose }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [category, setCategory] = useState(null);
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const appArea = getSafeSupportAppArea(location.pathname);
  const selectedCategory = category ? SUPPORT_CATEGORIES[category] : null;

  useEffect(() => {
    if (!isOpen) {
      setCategory(null);
      setMessage('');
      setStatus('idle');
      setError('');
    }
  }, [isOpen]);

  const chooseCategory = (nextCategory) => {
    setCategory(nextCategory);
    setMessage('');
    setStatus('idle');
    setError('');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!message.trim()) {
      setError(t('support_message_required', {
        defaultValue: 'Please enter a message.',
      }));
      return;
    }

    setStatus('loading');
    setError('');

    try {
      await api.post('/contact/support', {
        category,
        message: message.trim(),
        appArea,
      });
      setStatus('success');
    } catch (submissionError) {
      setStatus('error');
      setError(t('support_send_error', {
        defaultValue: 'Your message could not be sent. Please try again.',
      }));
    }
  };

  return (
    <BaseModalApp
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel={t('help_and_support', { defaultValue: 'Help & support' })}
      className="ReactModal__Content modal--app modal--500px help-support-modal"
    >
      <div className="modal-header">
        <h2>{t('help_and_support', { defaultValue: 'Help & support' })}</h2>
        <button
          type="button"
          className="modal-close-button"
          onClick={onRequestClose}
          aria-label={t('close', { defaultValue: 'Close' })}
        >
          <AVIcon icon="close" size={18} aria-hidden="true" />
        </button>
      </div>

      {!selectedCategory && (
        <>
          <p className="help-support-modal__intro">
            {t('support_intro', {
              defaultValue: 'Choose the kind of help you need.',
            })}
          </p>

          <div className="help-support-modal__options">
            {Object.entries(SUPPORT_CATEGORIES).map(([id, option]) => (
              <button
                key={id}
                type="button"
                className="help-support-modal__option"
                onClick={() => chooseCategory(id)}
              >
                <AVIcon icon={option.icon} size={22} aria-hidden="true" />
                <span>
                  <strong>{t(option.titleKey, { defaultValue: option.title })}</strong>
                  <small>{t(option.descriptionKey, { defaultValue: option.description })}</small>
                </span>
              </button>
            ))}
          </div>

          <p className="help-support-modal__external-note">
            {t('support_partnerships_note', {
              defaultValue: 'For partnerships, research collaborations, or institutional enquiries, use',
            })}{' '}
            <Link to="/contact" onClick={onRequestClose}>
              {t('contact_all_voiced', { defaultValue: 'Contact All-Voiced' })}
            </Link>.
          </p>
        </>
      )}

      {selectedCategory && status !== 'success' && (
        <form className="help-support-modal__form" onSubmit={handleSubmit}>
          <div className="help-support-modal__form-heading">
            <AVIcon icon={selectedCategory.icon} size={22} aria-hidden="true" />
            <div>
              <h3>{t(selectedCategory.titleKey, { defaultValue: selectedCategory.title })}</h3>
              <p>{selectedCategory.subject}</p>
            </div>
          </div>

          <label htmlFor="support-reply-email">
            {t('support_reply_email', { defaultValue: 'Email / reply address' })}
          </label>
          <input
            id="support-reply-email"
            className="app-modal-input"
            type="email"
            value={user?.email || ''}
            readOnly
            required
          />

          {appArea && (
            <div className="help-support-modal__context">
              <span>{t('support_app_area', { defaultValue: 'App area' })}</span>
              <code>{appArea}</code>
            </div>
          )}

          <label htmlFor="support-message">
            {t('support_message', { defaultValue: 'Message' })}
          </label>
          <p className="help-support-modal__guidance">
            {t(selectedCategory.guidanceKey, { defaultValue: selectedCategory.guidance })}
          </p>
          <textarea
            id="support-message"
            className="app-modal-input app-modal-textarea"
            rows="6"
            maxLength="5000"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            required
          />

          {error && (
            <ApplicationStateRegion
              density="compact"
              message={error}
              status={status === 'error' ? 'error' : 'validation'}
            />
          )}

          <div className="help-support-modal__actions">
            <ApplicationButton
              variant="quiet"
              onClick={() => chooseCategory(null)}
              disabled={status === 'loading'}
            >
              {t('back', { defaultValue: 'Back' })}
            </ApplicationButton>
            <ApplicationButton type="submit" disabled={status === 'loading'}>
              {status === 'loading'
                ? t('sending', { defaultValue: 'Sending…' })
                : t('send', { defaultValue: 'Send' })}
            </ApplicationButton>
          </div>
        </form>
      )}

      {status === 'success' && (
        <div className="help-support-modal__success">
          <ApplicationStateRegion
            status="success"
            message={t('support_message_sent', { defaultValue: 'Message sent' })}
            detail={t('support_message_sent_detail', {
              email: user?.email || '',
              defaultValue: `Thanks — we'll get back to you at ${user?.email || ''}.`,
            })}
          />
          <ApplicationButton variant="secondary" onClick={onRequestClose}>
            {t('close', { defaultValue: 'Close' })}
          </ApplicationButton>
        </div>
      )}
    </BaseModalApp>
  );
}
